import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Separator } from '../ui/separator';
import {
  Bell,
  User,
  Users,
  Shield,
  Calendar,
  Mail,
  Monitor,
  ExternalLink,
  Clock,
  Archive,
  CheckCircle,
  Circle,
} from 'lucide-react';
import { format } from 'date-fns';

const PRIORITY_STYLES = {
  LOW: { bg: 'bg-stone-100', text: 'text-gray-700', label: 'Low' },
  NORMAL: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Normal' },
  HIGH: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'High' },
  URGENT: { bg: 'bg-red-100', text: 'text-red-700', label: 'Urgent' },
};

const TYPE_STYLES = {
  GENERAL: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'General' },
  SYSTEM: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'System' },
};

const TARGET_ICONS = {
  ALL: Users,
  ROLE: Shield,
  USER: User,
};

const CHANNEL_LABELS = {
  DASHBOARD: 'Dashboard Only',
  EMAIL: 'Email Only',
  BOTH: 'Dashboard & Email',
};

export default function ViewNotificationDialog({ open, onOpenChange, notification }) {
  if (!notification) return null;

  const priorityStyle = PRIORITY_STYLES[notification.priority] || PRIORITY_STYLES.NORMAL;
  const typeStyle = TYPE_STYLES[notification.type] || TYPE_STYLES.GENERAL;
  const TargetIcon = TARGET_ICONS[notification.targetType] || Users;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return format(new Date(date), 'PPpp');
  };

  const getTargetLabel = () => {
    switch (notification.targetType) {
      case 'ALL':
        return 'All Users';
      case 'ROLE':
        return `Role: ${notification.targetRole || 'Unknown'}`;
      case 'USER':
        return notification.recipientName || 'Specific User';
      default:
        return 'Unknown';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Details
          </DialogTitle>
          <DialogDescription>
            View complete information about this notification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Status Badges */}
          <div className="flex flex-wrap gap-2">
            <Badge className={`${typeStyle.bg} ${typeStyle.text}`}>
              {typeStyle.label}
            </Badge>
            <Badge className={`${priorityStyle.bg} ${priorityStyle.text}`}>
              {priorityStyle.label} Priority
            </Badge>
            {notification.isArchived && (
              <Badge variant="outline" className="border-gray-300">
                <Archive className="h-3 w-3 mr-1" />
                Archived
              </Badge>
            )}
            <Badge variant={notification.isRead ? 'outline' : 'default'}>
              {notification.isRead ? (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Read
                </>
              ) : (
                <>
                  <Circle className="h-3 w-3 mr-1" />
                  Unread
                </>
              )}
            </Badge>
          </div>

          {/* Title and Message */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{notification.title}</h3>
            <p className="text-gray-600 whitespace-pre-wrap">{notification.message}</p>
          </div>

          {/* Action Link */}
          {notification.actionUrl && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <ExternalLink className="h-4 w-4 text-blue-600" />
              <a
                href={notification.actionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                {notification.actionText || notification.actionUrl}
              </a>
            </div>
          )}

          <Separator />

          {/* Target Information */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-500 uppercase">Target</h4>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <TargetIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{getTargetLabel()}</p>
                <p className="text-sm text-muted-foreground">
                  Channel: {CHANNEL_LABELS[notification.channel] || notification.channel}
                </p>
              </div>
            </div>
          </div>

          {/* Recipient (if USER target) */}
          {notification.targetType === 'USER' && notification.recipient && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-500 uppercase">Recipient</h4>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={notification.recipient.avatar} />
                    <AvatarFallback>
                      {getInitials(notification.recipientName)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{notification.recipientName || 'Unknown'}</p>
                    <p className="text-sm text-muted-foreground">
                      {notification.recipient.email}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Sender Information */}
          {notification.sentBy && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-500 uppercase">Sent By</h4>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={notification.senderAvatar} />
                    <AvatarFallback>
                      {getInitials(notification.senderName)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{notification.senderName || 'System'}</p>
                    <p className="text-sm text-muted-foreground">Administrator</p>
                  </div>
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Timestamps */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-500 uppercase">Timeline</h4>
            <div className="grid gap-3">
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Created:</span>
                <span>{formatDate(notification.createdAt)}</span>
              </div>
              {notification.isRead && notification.readAt && (
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-muted-foreground">Read:</span>
                  <span>{formatDate(notification.readAt)}</span>
                </div>
              )}
              {notification.isArchived && notification.archivedAt && (
                <div className="flex items-center gap-3 text-sm">
                  <Archive className="h-4 w-4 text-gray-500" />
                  <span className="text-muted-foreground">Archived:</span>
                  <span>{formatDate(notification.archivedAt)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
